import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Search, Loader2 } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';
import { useThemeStore } from '../../store/theme';

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  featured_image: string | null;
  thumbnail_image: string | null;
  category: string | null;
  tags: string[] | null;
  reading_time: number;
  published_at: string | null;
  author_name: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://sweetpilot.com';

export default function Blog() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/blog/posts`);
      if (!response.ok) throw new Error('Failed to fetch posts');
      const data = await response.json() as { success: boolean; data?: { posts: BlogPost[] } };
      setPosts(data.data?.posts || []);
    } catch (err) {
      setError('Failed to load blog posts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/blog/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json() as { success: boolean; data?: Category[] };
      setCategories(data.data || []);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const filteredPosts = posts.filter((post) => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // All posts displayed in the same grid layout
  const allPosts = filteredPosts;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getImagePlaceholder = (category: string | null) => {
    const placeholders: Record<string, string> = {
      'AI & Automation': '🤖',
      'Marketing': '📱',
      'Customer Success': '💬',
      'Product Updates': '🚀',
      'Case Studies': '📊',
      'Tips & Tutorials': '💡',
    };
    return placeholders[category || ''] || '📝';
  };

  return (
    <StaticPageLayout
      title="Blog"
      subtitle="Insights, tips, and stories about AI, automation, and the future of work."
    >
      {/* Search and Categories */}
      <section className="mb-12">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-12 pr-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${
                isDark
                  ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500'
                  : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
              }`}
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm transition-all ${
              selectedCategory === 'all'
                ? 'bg-purple-500 text-white'
                : isDark
                ? 'bg-dark-800 border border-white/10 text-gray-300 hover:border-purple-500/50'
                : 'bg-white border border-slate-200 text-slate-600 hover:border-purple-500/50'
            }`}
          >
            All
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.name)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                selectedCategory === category.name
                  ? 'bg-purple-500 text-white'
                  : isDark
                  ? 'bg-dark-800 border border-white/10 text-gray-300 hover:border-purple-500/50'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-purple-500/50'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className={`text-center py-20 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <p>{error}</p>
          <button
            onClick={fetchPosts}
            className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && posts.length === 0 && (
        <div className={`text-center py-20 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <p className="text-6xl mb-4">📝</p>
          <p className="text-xl font-semibold mb-2">No blog posts yet</p>
          <p>Check back soon for new content!</p>
        </div>
      )}

      {/* No Results */}
      {!loading && !error && posts.length > 0 && filteredPosts.length === 0 && (
        <div className={`text-center py-20 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
          <p className="text-xl font-semibold mb-2">No posts found</p>
          <p>Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Post Grid - All posts in uniform layout */}
      {!loading && allPosts.length > 0 && (
        <section className="mb-16">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`group rounded-2xl overflow-hidden transition-all ${
                  isDark
                    ? 'bg-dark-800/50 border border-white/5 hover:border-white/20'
                    : 'bg-white border border-slate-200 hover:border-slate-300 shadow-sm'
                }`}
              >
                <Link to={`/blog/${post.slug}`}>
                  <div className={`h-40 flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                    {post.thumbnail_image || post.featured_image ? (
                      <img
                        src={post.thumbnail_image || post.featured_image || ''}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <span className="text-6xl group-hover:scale-110 transition-transform">
                        {getImagePlaceholder(post.category)}
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      {post.category && (
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          isDark ? 'bg-dark-700 text-gray-400' : 'bg-slate-100 text-slate-600'
                        }`}>
                          {post.category}
                        </span>
                      )}
                    </div>
                    <h3 className={`text-lg font-semibold mb-2 transition-colors line-clamp-2 ${
                      isDark ? 'text-white group-hover:text-purple-300' : 'text-slate-900 group-hover:text-purple-600'
                    }`}>
                      {post.title}
                    </h3>
                    <p className={`text-sm mb-4 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {post.excerpt}
                    </p>
                    <div className={`flex items-center justify-between text-xs ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                      <span>{formatDate(post.published_at)}</span>
                      <span>{post.reading_time} min read</span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className={`text-center p-8 rounded-2xl border ${
        isDark
          ? 'bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border-white/5'
          : 'bg-gradient-to-br from-purple-50 to-cyan-50 border-slate-200'
      }`}>
        <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
          Subscribe to Our Newsletter
        </h2>
        <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
          Get the latest insights on AI, automation, and productivity delivered to your inbox.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <input
            type="email"
            placeholder="Enter your email"
            className={`flex-1 px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${
              isDark
                ? 'bg-dark-800 border-white/10 text-white placeholder-gray-500'
                : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
            }`}
          />
          <button className="px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white hover:opacity-90 transition-opacity">
            Subscribe
          </button>
        </div>
      </section>
    </StaticPageLayout>
  );
}
